import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui/tabs";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "./ui/breadcrumb";
import {
  Home,
  TrendingUp,
  Target,
  Database,
  CheckCircle,
  FileText,
  Calendar,
} from "lucide-react";
import {
  subDays,
  startOfToday,
  endOfToday,
  format,
} from "date-fns";

// Import dashboard components
import { DashboardKPICards, KPIData } from "./dashboard/DashboardKPICards";
import {
  DashboardFiltersComponent,
  DashboardFilters,
} from "./dashboard/DashboardFilters";
import { ChartTypeBreakdownItem, OverviewTab } from "./dashboard/tabs/OverviewTab";
import { QualityMetric, QualityTab } from "./dashboard/tabs/QualityTab";
import { BatchesTab, batchParams } from "./dashboard/tabs/BatchesTab";

// Import mock data
import {
  mockBatchData,
  calculateKPIs,
  getTimeSeriesData,
  BatchRunData,
} from "../src/Utils/dashboardMockData";
import { apiClient } from "@/Utils/apiClient";
import { useAuth } from "./AuthContext";
import { buildQuery, flattenFilters } from "@/Utils/helpers";
import { useDebounce } from "@/hooks/debounce";

interface HoverCardProps {
  children: React.ReactNode;
  content: React.ReactNode;
}
// Today's metrics card component
function TodayMetricsCard({ data }: { data: any }) {
  // const today = startOfToday();
  // const todayData = data.filter((batch) => {
  //   const batchDate = startOfToday();
  //   batchDate.setTime(batch.date.getTime());
  //   return batchDate.getTime() === today.getTime();
  // });
  console.log(data, "data")

  // const todayMetrics = calculateKPIs(todayData);

  // // Calculate enhanced metrics
  // const extracted = Math.floor(
  //   todayMetrics.totalProcessed * 1.15,
  // );
  // const aiAudited = todayMetrics.totalProcessed;
  // const auditorAudited = Math.floor(
  //   todayMetrics.totalProcessed * 0.85,
  // );
  // const aiApproved = todayMetrics.approved;
  // const auditorApproved = Math.floor(
  //   todayMetrics.approved * 0.85,
  // );
  // const aiCorrection = todayMetrics.sentForCorrection;
  // const auditorCorrection = Math.floor(
  //   todayMetrics.sentForCorrection * 0.72,
  // );
  const HoverCard: React.FC<HoverCardProps> = ({ children, content }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    return (
      <div
        ref={ref}
        className="relative inline-block"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
        {open && (
          <div
            className="absolute left-0 top-full mt-2 z-50 w-48 rounded-2xl bg-white p-3 shadow-xl border text-left"
          >
            {content}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <CardHeader className="pb-1 pt-2 px-3">
        <CardTitle className="flex items-center gap-1.5 text-blue-900 text-sm">
          <Calendar className="w-3 h-3" />
          Today's Activity
          <Badge
            variant="outline"
            className="ml-auto text-xs text-blue-700 border-blue-300 py-0 px-1.5 h-4"
          >
            {format(new Date(), "MMM dd")}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 pb-2 px-3">
        <div className="grid grid-cols-4 gap-2 text-center">
          {/* Extracted */}
          <div>
            <div className="text-xs text-purple-700 font-medium leading-tight">
              Extracted
            </div>
            <div className="text-base font-bold text-purple-900 leading-none">

              <HoverCard content={
                <div className="text-sm space-y-1 text-black">
                  <p className="font-semibold">Extraction Details</p>
                  <p className="font-medium">Succeeded: {data?.chartsExtracted.successful ?? 0}</p>
                  <p className="font-medium">Failed: {data?.chartsExtracted?.failed ?? 0}</p>
                </div>
              }>

                {data?.chartsExtracted?.total?.toLocaleString() || 0}
              </HoverCard>
            </div>
          </div>

          {/* Audited (AI vs Auditor) */}
          <div className="border-l border-blue-200/60 pl-2">
            <div className="text-xs text-blue-700 font-medium leading-tight">
              Audited
            </div>
            <div className="text-xs text-blue-600 leading-tight">
              AI: <HoverCard content={
                <div className="text-sm space-y-1 text-black">
                  <p className="font-semibold">AI Processed Details</p>
                  <p>Success: {data?.audited?.aiProcessed?.successful ?? 0}</p>
                  <p>Failed: {data?.audited?.aiProcessed?.failed ?? 0}</p>
                </div>
              }>

                {data?.audited?.aiProcessed?.total?.toLocaleString() || 0}

              </HoverCard> | Auditor:{" "}
              {data?.audited?.auditorReviewed?.toLocaleString()}
            </div>
          </div>

          {/* Approved (AI vs Auditor) */}
          <div className="border-l border-blue-200/60 pl-2">
            <div className="text-xs text-green-700 font-medium leading-tight">
              Approved
            </div>
            <div className="text-xs text-green-600 leading-tight">
              AI: {data?.approved?.aiApproved.toLocaleString()} | Auditor:{" "}
              {data?.approved?.auditorApproved.toLocaleString()}
            </div>
          </div>

          {/* Correction (AI vs Auditor) */}
          <div className="border-l border-blue-200/60 pl-2">
            <div className="text-xs text-orange-700 font-medium leading-tight">
              Correction
            </div>
            <div className="text-xs text-orange-600 leading-tight">
              AI: {data?.correction?.aiSentForCorrection.toLocaleString()} | Auditor:{" "}
              {data?.correction?.auditorApproved.toLocaleString()}
            </div>
          </div>
        </div>

        {!data && (
          <div className="text-center py-1 text-blue-600 mt-2 border-t border-blue-200/60">
            <div className="text-xs leading-tight">
              No processing activity recorded for today
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
export interface DashboardData {
  batchesTab: batchParams;
  qualityTab: QualityMetric;
  overviewTab: ChartTypeBreakdownItem;
  dashboardMetrics: KPIData
}
function DashboardView() {
  // Initialize filters with last 30 days as default
  const [filters, setFilters] = useState<DashboardFilters>({
    dateRange: {
      from: subDays(new Date(), 29),
      to: new Date(),
    },
    program: "all",
    batchType: "real-run",
    status: "all",
    searchTerm: '',
    batchStatus: '',
    auditorView: false,
  });
  const [dashBoardData, setDashboardData] = useState({
    qualityTab: {
      overviewData: {
        aiDecisionAccuracy: { accuracy: 0, totalCorrectAiDecisions: 0, totalChartsReviewed: 0 },
        auditCoverage: { coverage: 0, totalAiDecisions: 0, totalChartsReviewed: 0 },
        falsePositiveRate: { rate: 0, totalAiApproved: 0, falsePositiveCount: 0 },
        falseNegativeRate: { rate: 0, totalAiSendForCorrection: 0, falseNegativeCount: 0 }
      },
      trendData: []
    },
    overviewTab: {},
    batchesTab: { batches: [], summary: { totalBatches: 0, runningBatches: 0, failedBatches: 0 } },
    dashboardMetrics: {}

  })

  const [todayMetrics, setTodayMetrics] = useState(null);

  const [activeTab, setActiveTab] = useState("overview");
  const { token } = useAuth()

  const preprocessFilters = (filters: any) => {
    const copy = { ...filters };

    if (copy.program === "all") copy.program = null;
    if (copy.status === "all") copy.status = null;
    if (copy.batchStatus === "all") copy.batchStatus = null;
    if (copy.auditorView) {
      copy.auditorView = "auditor";
    } else {
      copy.auditorView = null;
    }

    return copy;
  };
  const fetchData = async () => {
    const flat = flattenFilters(filters);
    const cleaned = preprocessFilters(flat);
    const query = buildQuery(cleaned, {
      from: "fromDate",
      to: "toDate",
      program: "chartType",
      batchType: "batchType",
      status: "decisionType",
      searchTerm: "search",
      batchStatus: "batchStatus",
      auditorView: "view",
    });

    const response = await apiClient(`dashboard/metrics?${query}`, {
      method: 'GET',
      token
    })
    const result = await response.json();
    setDashboardData(prev => ({ ...prev, dashboardMetrics: result }))

  }
  const fetchQualityData = async () => {
    const flat = flattenFilters(filters);
    const cleaned = preprocessFilters(flat);
    const query = buildQuery(cleaned, {
      from: "fromDate",
      to: "toDate",
      program: "chartType",
      batchType: "batchType",
      status: "decisionType",
      searchTerm: "search",
      batchStatus: "batchStatus",
      auditorView: "view",
    });
    const response = await apiClient(`dashboard/quality/metrics?${query}`, {
      method: 'GET',
      token
    })
    const result = await response.json()
    setDashboardData(prev => ({ ...prev, qualityTab: result?.data }))
  }
  const fetchOverviewData = async () => {
    const flat = flattenFilters(filters);
    const cleaned = preprocessFilters(flat);
    const query = buildQuery(cleaned, {
      from: "fromDate",
      to: "toDate",
      program: "chartType",
      batchType: "batchType",
      status: "decisionType",
      searchTerm: "search",
      batchStatus: "batchStatus",
      auditorView: "view",
    });
    const response = await apiClient(`dashboard/overview/metrics?${query}`, {
      method: 'GET',
      token
    })
    const result = await response.json()
    setDashboardData(prev => ({ ...prev, overviewTab: result?.data }))
  }
  const fetchBatchesData = async (filters: any) => {
    const flat = flattenFilters(filters);
    const cleaned = preprocessFilters(flat);
    const query = buildQuery(cleaned, {
      from: "fromDate",
      to: "toDate",
      program: "chartType",
      batchType: "batchType",
      status: "decisionType",
      searchTerm: "search",
      batchStatus: "batchStatus",
      auditorView: "view",
    });
    const response = await apiClient(`dashboard/batches?${query}`, {
      method: 'GET',
      token
    })
    const result = await response.json()
    setDashboardData(prev => ({ ...prev, batchesTab: result.data }))
  }
  const fetchTodayMetricsData = async () => {
    try {
      // const flat = flattenFilters(filters);
      // const cleaned=  preprocessFilters(flat);
      // const query = buildQuery(cleaned, {
      //  from: "fromDate",
      //  to: "toDate",
      //  program: "chartType",
      //  batchType: "batchType",
      //  status: "decisionType",
      //  searchTerm: "search",
      //  batchStatus: "batchStatus"
      // });
      const response = await apiClient(`dashboard/today/metrics`, {
        method: 'GET',
        token
      })
      const result = await response.json();
      setTodayMetrics(result.todayMetrics);
    } catch (error) {
      console.error("Error fetching today's metrics:", error);
    }
  }

  //   useEffect(()=>{ 
  // fetchData()
  // fetchQualityData()
  // fetchOverviewData()
  // fetchBatchesData()
  //   },[filters])
  useEffect(() => {
    fetchData();
  }, [filters.dateRange, filters.auditorView]);

  useEffect(() => {
    fetchTodayMetricsData();
  }, [])

  useEffect(() => {
    if (activeTab === "overview") {
      fetchOverviewData();
    } else if (activeTab === "quality") {
      fetchQualityData();
    }
  }, [filters.dateRange, filters.program, filters.batchType, filters.status, filters.auditorView, activeTab]);
  const debouncedSearch = useDebounce(filters.searchTerm, 500);

  useEffect(() => {
    if (activeTab === "batches") {
      fetchBatchesData({
        ...filters,
        searchTerm: debouncedSearch,
      });
    }
  }, [
    filters.dateRange,
    filters.program,
    filters.batchType,
    filters.status,
    filters.batchStatus,
    filters.auditorView,
    debouncedSearch,
    activeTab,
  ]);
  // Filter data based on current filters
  const filteredData = useMemo(() => {
    let data = mockBatchData;

    // Date range filter
    if (filters.dateRange.from && filters.dateRange.to) {
      data = data.filter(
        (batch) =>
          batch.date >= filters.dateRange.from &&
          batch.date <= filters.dateRange.to,
      );
    }

    // Program filter
    if (filters.program !== "all") {
      // Map the filter values to mock data programs for demonstration
      const programMapping: Record<string, string[]> = {
        "csc-section-28": ["Medicare", "Medicaid"], // Example mapping
        "dls-section-17": ["Commercial", "ACA"], // Example mapping
        rn: ["Workers Comp", "Auto"], // Example mapping
      };

      const mappedPrograms = programMapping[filters.program];
      if (mappedPrograms) {
        data = data.filter((batch) =>
          mappedPrograms.includes(batch.program),
        );
      }
    }

    // Batch Type filter (simulate with mock data - in real implementation would filter based on batch type)
    if (filters.batchType !== "real-run") {
      // For demo purposes, filter based on batch type logic
      // In real implementation, this would be a proper field in the data
      data = data.filter((batch) => {
        if (filters.batchType === "test-run") {
          // Show a subset of data for test runs
          return (
            batch.batchId.includes("TEST") ||
            Math.random() < 0.3
          );
        }
        return true; // real-run shows all data
      });
    }

    // Status filter
    if (filters.status !== "all") {
      if (filters.status === "approved") {
        data = data.filter((batch) => batch.approved > 0);
      } else if (filters.status === "correction") {
        data = data.filter(
          (batch) => batch.sentForCorrection > 0,
        );
      }
    }

    return data;
  }, [filters]);

  // Calculate previous period data for KPI trends
  const previousPeriodData = useMemo(() => {
    if (!filters.dateRange.from || !filters.dateRange.to)
      return [];

    const periodDays = Math.ceil(
      (filters.dateRange.to.getTime() -
        filters.dateRange.from.getTime()) /
      (1000 * 60 * 60 * 24),
    );
    const previousEnd = subDays(filters.dateRange.from, 1);
    const previousStart = subDays(previousEnd, periodDays);

    return mockBatchData.filter(
      (batch) =>
        batch.date >= previousStart &&
        batch.date <= previousEnd,
    );
  }, [filters.dateRange]);

  // Calculate KPIs for current and previous periods
  const currentKPIs: KPIData = {
    extractionMetrics: dashBoardData?.dashboardMetrics?.extractionMetrics,
    processingMetrics: dashBoardData?.dashboardMetrics?.processingMetrics,
    approvalMetrics: dashBoardData?.dashboardMetrics?.approvalMetrics,
    correctionMetrics: dashBoardData?.dashboardMetrics?.correctionMetrics,
    todayMetrics: dashBoardData?.dashboardMetrics?.todayMetrics

  };
  const previousKPIs = calculateKPIs(previousPeriodData);

  // Get time series data
  const timeSeriesData = getTimeSeriesData(filteredData, "day");

  // Filter options are now hardcoded in the component

  // Validate data before rendering
  const hasValidData = filteredData && filteredData.length > 0;
  const hasValidTimeSeriesData =
    timeSeriesData && timeSeriesData.length > 0;
  const batchesTabData: batchParams = {
    batches: dashBoardData?.batchesTab?.batches,
    summary: dashBoardData?.batchesTab?.summary
  };
  const qualityTabData: QualityMetric = {
    overviewData: dashBoardData?.qualityTab?.overviewData,
    trendData: dashBoardData?.qualityTab?.trendData

  }
  const overviewTabData: ChartTypeBreakdownItem = {
    chartTypeDistribution: dashBoardData?.overviewTab?.chartTypeDistribution,
    chartTypeBreakdown: dashBoardData?.overviewTab?.chartTypeBreakdown,
    processingVolumeTrends: dashBoardData?.overviewTab?.processingVolumeTrends,
    chartTypePerformance: dashBoardData?.overviewTab?.chartTypePerformance,
    aiAuditorAgreementTrends: dashBoardData?.overviewTab?.aiAuditorAgreementTrends


  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
      <div className="p-6">
        {/* Breadcrumb Navigation */}
        <div className="mb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage className="flex items-center gap-2 text-sm">
                  <Home className="w-4 h-4" />
                  Dashboard
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text tracking-tight leading-tight">
                Healthcare Chart Review Dashboard
              </h1>
              <p className="text-base text-gray-600 max-w-3xl leading-relaxed">
                Real-time operational visibility for chart
                review workflows, quality monitoring, and audit
                compliance
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-">
                Last updated: {new Date().toLocaleTimeString()}
              </Badge>
            </div>
          </div>
        </div>

        {/* Today's Metrics Card */}
        <div className="mb-6">
          <TodayMetricsCard data={todayMetrics} />
        </div>

        {/* Filters */}
        <DashboardFiltersComponent
          filters={filters}
          onFiltersChange={setFilters}
        />

        {/* KPI Cards */}
        <div className="mb-6">
          <DashboardKPICards
            data={currentKPIs}
          />
        </div>

        {/* Main Content Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger
              value="overview"
              className="flex items-center gap-2 text-base"
            >
              <TrendingUp className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="quality"
              className="flex items-center gap-2 text-base"
            >
              <Target className="w-4 h-4" />
              Quality
            </TabsTrigger>
            <TabsTrigger
              value="batches"
              className="flex items-center gap-2 text-base"
            >
              <Database className="w-4 h-4" />
              Batches
            </TabsTrigger>
          </TabsList>

          <div className="mt-4">
            <TabsContent value="overview" className="space-y-4">
              <OverviewTab
                data={overviewTabData}
              />
            </TabsContent>

            <TabsContent value="quality" className="space-y-4">
              <QualityTab
                data={qualityTabData}

              />
            </TabsContent>

            <TabsContent value="batches" className="space-y-4">
              <BatchesTab data={batchesTabData} filters={filters} setFilters={setFilters} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

export default DashboardView;